/**
 * The back-office Inventory list's filters and sort: the state the Filters
 * sheet edits, the chips the list shows for it, and the query string the
 * owner product endpoints take.
 */

export type StockStatusFilter = 'all' | 'low' | 'out';

export type InventorySort =
  | 'newest'
  | 'name_asc'
  | 'name_desc'
  | 'stock_asc'
  | 'stock_desc'
  | 'price_asc'
  | 'price_desc';

export type ShowOnlyKey =
  | 'favourites'
  | 'services'
  | 'noMinimumStockLevel'
  | 'noBuyingPrice'
  | 'noBarcode'
  | 'noImage';

export type InventoryFilters = {
  stockStatus: StockStatusFilter;
  /** Category names; `UNCATEGORISED` stands for products with no category. */
  categories: string[];
  subCategories: string[];
  showOnly: ShowOnlyKey[];
};

export type StockStatusCounts = { all: number; low: number; out: number };

/** Sent in `categories` for products with no category (the server's token). */
export const UNCATEGORISED = '__none__';
export const UNCATEGORISED_LABEL = 'Uncategorised';

export const DEFAULT_INVENTORY_SORT: InventorySort = 'newest';

export const DEFAULT_INVENTORY_FILTERS: InventoryFilters = {
  stockStatus: 'all',
  categories: [],
  subCategories: [],
  showOnly: [],
};

export const STOCK_STATUS_OPTIONS: { key: StockStatusFilter; label: string }[] =
  [
    { key: 'all', label: 'All' },
    { key: 'low', label: 'Low stock' },
    { key: 'out', label: 'Out of stock' },
  ];

export const SORT_OPTIONS: { key: InventorySort; label: string }[] = [
  { key: 'newest', label: 'Newest' },
  { key: 'name_asc', label: 'Name A–Z' },
  { key: 'name_desc', label: 'Name Z–A' },
  { key: 'stock_asc', label: 'Lowest stock' },
  { key: 'stock_desc', label: 'Highest stock' },
  { key: 'price_asc', label: 'Lowest price' },
  { key: 'price_desc', label: 'Highest price' },
];

export const SHOW_ONLY_OPTIONS: { key: ShowOnlyKey; label: string }[] = [
  { key: 'favourites', label: 'Favourites' },
  { key: 'services', label: 'Services' },
  { key: 'noMinimumStockLevel', label: 'No minimum stock level' },
  { key: 'noBuyingPrice', label: 'No buying price' },
  { key: 'noBarcode', label: 'No barcode' },
  { key: 'noImage', label: 'No image' },
];

const SHOW_ONLY_PARAMS: Record<ShowOnlyKey, string> = {
  favourites: 'bp_favourites',
  services: 'bp_services',
  noMinimumStockLevel: 'bp_no_minimum_stock_level',
  noBuyingPrice: 'bp_no_buying_price',
  noBarcode: 'bp_no_barcode',
  noImage: 'bp_no_image',
};

export const getSortLabel = (sort: InventorySort): string =>
  SORT_OPTIONS.find(option => option.key === sort)?.label ||
  SORT_OPTIONS[0].label;

export const getCategoryLabel = (category: string): string =>
  category === UNCATEGORISED ? UNCATEGORISED_LABEL : category;

export type AppliedFilterChip =
  | { id: string; label: string; kind: 'status' }
  | { id: string; label: string; kind: 'category'; value: string }
  | { id: string; label: string; kind: 'subCategory'; value: string }
  | { id: string; label: string; kind: 'showOnly'; value: ShowOnlyKey };

/**
 * One removable chip per active filter, in the order the list shows them:
 * stock status, categories, subcategories, show-only. "All" has no chip.
 */
export const getAppliedFilterChips = (
  filters: InventoryFilters,
): AppliedFilterChip[] => {
  const chips: AppliedFilterChip[] = [];
  if (filters.stockStatus !== 'all') {
    const option = STOCK_STATUS_OPTIONS.find(
      o => o.key === filters.stockStatus,
    );
    chips.push({
      id: 'status',
      label: option?.label || filters.stockStatus,
      kind: 'status',
    });
  }
  filters.categories.forEach(category => {
    chips.push({
      id: `category:${category}`,
      label: `Category: ${getCategoryLabel(category)}`,
      kind: 'category',
      value: category,
    });
  });
  filters.subCategories.forEach(subCategory => {
    chips.push({
      id: `subCategory:${subCategory}`,
      label: `Category: ${subCategory}`,
      kind: 'subCategory',
      value: subCategory,
    });
  });
  filters.showOnly.forEach(key => {
    const option = SHOW_ONLY_OPTIONS.find(o => o.key === key);
    chips.push({
      id: `showOnly:${key}`,
      label: option?.label || key,
      kind: 'showOnly',
      value: key,
    });
  });
  return chips;
};

export const countActiveFilters = (filters: InventoryFilters): number =>
  getAppliedFilterChips(filters).length;

export const hasActiveFilters = (filters: InventoryFilters): boolean =>
  countActiveFilters(filters) > 0;

/** The filters with one chip taken off. */
export const removeFilterChip = (
  filters: InventoryFilters,
  chip: AppliedFilterChip,
): InventoryFilters => {
  switch (chip.kind) {
    case 'status':
      return { ...filters, stockStatus: 'all' };
    case 'category':
      return {
        ...filters,
        categories: filters.categories.filter(c => c !== chip.value),
      };
    case 'subCategory':
      return {
        ...filters,
        subCategories: filters.subCategories.filter(c => c !== chip.value),
      };
    case 'showOnly':
      return {
        ...filters,
        showOnly: filters.showOnly.filter(k => k !== chip.value),
      };
    default:
      return filters;
  }
};

export const toggleShowOnly = (
  filters: InventoryFilters,
  key: ShowOnlyKey,
): InventoryFilters => ({
  ...filters,
  showOnly: filters.showOnly.includes(key)
    ? filters.showOnly.filter(k => k !== key)
    : [...filters.showOnly, key],
});

export const toggleCategory = (
  filters: InventoryFilters,
  category: string,
): InventoryFilters => ({
  ...filters,
  categories: filters.categories.includes(category)
    ? filters.categories.filter(c => c !== category)
    : [...filters.categories, category],
});

export const areFiltersEqual = (
  a: InventoryFilters,
  b: InventoryFilters,
): boolean =>
  a.stockStatus === b.stockStatus &&
  sameSet(a.categories, b.categories) &&
  sameSet(a.subCategories, b.subCategories) &&
  sameSet(a.showOnly, b.showOnly);

const sameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every(item => b.includes(item));

/**
 * Query string for the owner product list and search endpoints, starting
 * with "&" so it appends to `?page=…&limit=…`. Empty for the defaults, which
 * is what the endpoints returned before filters existed.
 */
export const buildInventoryListQuery = (
  filters: InventoryFilters,
  sort: InventorySort,
  /** `includeCounts`: ask for per-status counts (page 1 only; the Filters sheet). */
  options: { includeCounts?: boolean } = {},
): string => {
  const parts: string[] = [];
  if (filters.stockStatus !== 'all') {
    parts.push(`bp_stock_status=${filters.stockStatus}`);
  }
  if (filters.categories.length) {
    parts.push(
      `bp_categories=${encodeURIComponent(filters.categories.join(','))}`,
    );
  }
  if (filters.subCategories.length) {
    parts.push(
      `bp_sub_categories=${encodeURIComponent(
        filters.subCategories.join(','),
      )}`,
    );
  }
  filters.showOnly.forEach(key => {
    parts.push(`${SHOW_ONLY_PARAMS[key]}=true`);
  });
  if (sort !== DEFAULT_INVENTORY_SORT) {
    parts.push(`bp_sort=${sort}`);
  }
  if (options.includeCounts) {
    parts.push('bp_include_counts=true');
  }
  return parts.length ? `&${parts.join('&')}` : '';
};

/**
 * Query string for the stock value endpoint: the filters only, starting with
 * "?". Empty for the defaults, which is the whole store.
 */
export const buildStockValueQuery = (filters: InventoryFilters): string => {
  const query = buildInventoryListQuery(filters, DEFAULT_INVENTORY_SORT);
  return query ? `?${query.slice(1)}` : '';
};

/** The strip's and the report's title: the whole store, or the filtered products. */
export const getStockValueTitle = (filters?: InventoryFilters | null): string =>
  filters && hasActiveFilters(filters)
    ? 'Filtered stock value'
    : 'Total stock value';

/** "Out of stock, Baby care and Favourites" for the no-matches message. */
export const describeFilters = (filters: InventoryFilters): string => {
  const labels = getAppliedFilterChips(filters).map(chip =>
    chip.kind === 'category' || chip.kind === 'subCategory'
      ? chip.label.replace(/^Category: /, '')
      : chip.label,
  );
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/** The filters with a show-only key on; unchanged when it already is. */
export const addShowOnly = (
  filters: InventoryFilters,
  key: ShowOnlyKey,
): InventoryFilters =>
  filters.showOnly.includes(key)
    ? filters
    : { ...filters, showOnly: [...filters.showOnly, key] };

/**
 * The filters for the products a category row of the Stock value report
 * counted: the current filters narrowed to that category. A row reached only
 * through selected subcategories keeps those subcategories (the ones under
 * the category, when known) rather than widening to the whole category.
 */
export const narrowFiltersToCategory = (
  filters: InventoryFilters,
  category: string,
  subCategoriesOf: (category: string) => string[],
): InventoryFilters => {
  const viaSubCategories =
    filters.subCategories.length > 0 && !filters.categories.includes(category);
  if (!viaSubCategories) {
    return { ...filters, categories: [category], subCategories: [] };
  }
  const known = subCategoriesOf(category);
  const kept = filters.subCategories.filter(sub => known.includes(sub));
  return {
    ...filters,
    categories: [],
    subCategories: kept.length ? kept : filters.subCategories,
  };
};

/** `{ all, low, out }` from a list response; null when the server sent none. */
export const parseStockStatusCounts = (
  payload: any,
): StockStatusCounts | null => {
  const counts = payload?.counts;
  if (!counts || typeof counts !== 'object') return null;
  const toCount = (value: unknown) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    all: toCount(counts.all),
    low: toCount(counts.low),
    out: toCount(counts.out),
  };
};

const STOCK_STATUS_FILTERS: StockStatusFilter[] = ['all', 'low', 'out'];

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(item => String(item ?? '').trim()).filter(Boolean)
    : [];

/** A full set of filters from route params; null unless it is an object. */
const parseInventoryFilters = (value: unknown): InventoryFilters | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const status = String(raw.stockStatus ?? 'all') as StockStatusFilter;
  return {
    stockStatus: STOCK_STATUS_FILTERS.includes(status) ? status : 'all',
    categories: stringList(raw.categories),
    subCategories: stringList(raw.subCategories),
    showOnly: stringList(raw.showOnly).filter((key): key is ShowOnlyKey =>
      SHOW_ONLY_OPTIONS.some(option => option.key === key),
    ),
  };
};

/**
 * Filters from the Inventory tab's route params: the Summary cards open the
 * list with a preset, and the Stock value report with its full `filters`.
 */
export const filtersFromRouteParams = (
  params: Record<string, unknown> | undefined,
): InventoryFilters | null => {
  if (!params) return null;
  const full = parseInventoryFilters(params.filters);
  if (full) return full;
  const preset = String(params.preset ?? '').toLowerCase();
  const category = String(params.category ?? '').trim();
  const showOnly = String(params.showOnly ?? '').trim() as ShowOnlyKey;
  if (!preset && !category && !showOnly) return null;
  return {
    stockStatus: preset.includes('out')
      ? 'out'
      : preset.includes('low')
        ? 'low'
        : 'all',
    categories: category ? [category] : [],
    subCategories: [],
    showOnly: SHOW_ONLY_OPTIONS.some(o => o.key === showOnly) ? [showOnly] : [],
  };
};
